import type {
	Group,
	GroupBackgroundStyleVariant,
	GroupSettings,
} from "@gik/core/models/gik/Group";
import { logoImageUrl } from "@gik/inkind-page/constants";
import { defaultGradient } from "@gik/ui-legacy/ColorPicker/GradientPicker";
import React from "react";

// ponytail: defaults MUST match the branding pickers so the live preview
// reflects the actual selection. Solid uses the Figma placeholder color
// (#399EB4, per GIK-11017); gradient reuses the GradientPicker default.
const defaultBrandColor = "#399EB4";

export function useGroupDefaults(group: Group): Group {
	return React.useMemo(() => {
		if (!group) return null;
		const newGroup = { ...group };

		if (!group.inkindGroupSettings?.brandColor) {
			newGroup.inkindGroupSettings = {
				...(newGroup.inkindGroupSettings ?? {}),
				brandColor: defaultBrandColor,
				// only fall back to 'solid' when NO variant was chosen —
				// otherwise a missing brandColor clobbers an image/gradient pick.
				styleVariant: (group.inkindGroupSettings?.styleVariant ??
					"solid") as GroupBackgroundStyleVariant,
			} as GroupSettings;
		}

		if (!group.inkindGroupSettings?.gradientColor1) {
			newGroup.inkindGroupSettings = {
				...(newGroup.inkindGroupSettings ?? {}),
				gradientColor1: defaultGradient[0].color,
			} as GroupSettings;
		}

		if (!group.inkindGroupSettings?.gradientColor2) {
			newGroup.inkindGroupSettings = {
				...(newGroup.inkindGroupSettings ?? {}),
				gradientColor2: defaultGradient[1].color,
			} as GroupSettings;
		}

		if (!group.inkindGroupSettings?.logoImageUrl) {
			newGroup.inkindGroupSettings = {
				...(newGroup.inkindGroupSettings ?? {}),
				logoImageUrl: logoImageUrl,
			} as GroupSettings;
		}

		// ponytail: icon/logomark is retired — always mirror the logo so any
		// remaining iconImageUrl reader resolves to the group's logo.
		newGroup.inkindGroupSettings = {
			...(newGroup.inkindGroupSettings ?? {}),
			iconImageUrl:
				newGroup.inkindGroupSettings?.logoImageUrl ?? logoImageUrl,
		} as GroupSettings;

		return newGroup;
	}, [group]);
}
